import { useMemo } from 'react';

import type { NotificationsPaginationParams } from '~/components/Notifications/hooks/useNotificationsPagination';
import { useNotificationsPagination } from '~/components/Notifications/hooks/useNotificationsPagination';
import type { ExposureModel } from '~/components/types/exposure-types';

export interface CalendarNotificationsParams extends Omit<NotificationsPaginationParams, 'claimId'> {
  dateRange: {
    start: string;
    end: string;
  };
  userId?: string;
}

export const useCalendarNotifications = (params: CalendarNotificationsParams) => {
  const { dateRange, userId, ...paginationParams } = params;

  // Use large page size to get all notifications in date range
  const {
    pageResults: notifications,
    pageMeta,
    isLoading,
    isError,
    reload,
    updatePageResult,
  } = useNotificationsPagination(
    {
      ...paginationParams,
      startDueDateTime: dateRange.start,
      endDueDateTime: dateRange.end,
      adjustersIds: userId ? [userId] : [],
    },
    1000 // Large page size to get all notifications
  );

  // Transform exposures array to dictionary for calendar use
  const exposuresDict = useMemo(() => {
    const dict: { [key: string]: ExposureModel } = {};
    (pageMeta?.exposures || []).forEach((exposure) => {
      // Type assertion since auto-generated types use object[] but we know it's ExposureModel[]
      const typedExposure = exposure as ExposureModel;
      if (typedExposure.id) {
        dict[typedExposure.id] = typedExposure;
      }
    });
    return dict;
  }, [pageMeta?.exposures]);

  return {
    notifications: notifications || [],
    exposuresDict,
    isLoading,
    isError,
    reload,
    updateNotification: updatePageResult,
  };
};
